import type { School, TierThresholds } from "./types";
import { raceTimeToSeconds } from "./format";

export type Tier = "dream" | "stretch" | "target" | "floor" | "unknown";

export const TIER_LABELS: Record<Tier, string> = {
  dream: "Dream",
  stretch: "Stretch",
  target: "Target Range",
  floor: "Likely Floor",
  unknown: "No 2k data",
};

/**
 * Ported from the project methodology: compares a school's cMax "combined seconds back"
 * against Katie's projected range. Thresholds are configurable in Settings so tiers shift
 * as her times improve.
 *   secs-back < dream           -> Dream     (their 2V is well ahead of her projected pace)
 *   dream..stretch              -> Stretch   (amber in-between — a real reach but not absurd)
 *   stretch..target             -> Target Range
 *   > target                    -> Likely Floor
 *
 * Falls back to sensible defaults if a stale state.json is missing the newer keys.
 */
export function fitTier(school: School, thresholds: TierThresholds): Tier {
  const s = school.cmax_secs_back_combined;
  if (s === null || s === undefined) return "unknown";
  const dream = thresholds.dream ?? 50;
  const stretch = thresholds.stretch ?? 55;
  if (s < dream) return "dream";
  if (s < stretch) return "stretch";
  if (s <= thresholds.target) return "target";
  return "floor";
}

/**
 * Every school's `est_8_2k_time` is modeled as a single anchor (the #1 national boat's
 * estimated 8+ 2k) plus that school's cMax seconds-back. So a fit-tier threshold expressed
 * in seconds-back maps to an est-2k time of `anchor + threshold`. Derived from the data so
 * it self-corrects if the model changes; falls back to 5:47.7.
 */
export function est2kAnchorSeconds(
  schools: Pick<School, "est_8_2k_time" | "cmax_secs_back_combined">[],
): number {
  for (const s of schools) {
    const k = raceTimeToSeconds(s.est_8_2k_time);
    if (k != null && s.cmax_secs_back_combined != null) {
      return Math.round((k - s.cmax_secs_back_combined) * 10) / 10;
    }
  }
  return 347.7;
}

export const TIER_ORDER: Record<Tier, number> = {
  dream: 0,
  stretch: 1,
  target: 2,
  floor: 3,
  unknown: 4,
};
